import { AxiosError } from 'axios'
import { action, computed, flow, makeObservable, observable } from 'mobx'

import { openPdfDocumentFromBlob, requestWrapper } from 'Shared/utils'

import { DataReport } from 'Core/common/DataReport/types'
import { formatReportParameters } from 'Core/common/DataReport/utils/helpers'
import { STORE_UI } from 'Core/constants'
import { CustomGenerator, LoadingBtnNames } from 'Core/interfaces'
import {
	formatDataToLabelValue,
	formatValueDescriptionDataToLabelValue,
	getDefaultStoreArrayValue,
	getDefaultStoreValue
} from 'Core/utils'

import { ReportRepo } from 'Portals/Supplier/repositories'
import { RootStore } from 'Portals/Supplier/stores'
import i18n from 'Portals/Supplier/utils/i18nService'

import { ReportTable } from './ReportTable'

export class ReportStore {
	table: ReportTable
	rootStore: RootStore
	@observable accessor reportNames = getDefaultStoreArrayValue()
	@observable accessor reportData = getDefaultStoreValue<DataReport.Data>()
	@observable accessor isReportLogDownloading: boolean = false

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore
		this.table = new ReportTable(rootStore)
	}

	@flow.bound
	*retrieveReportNameOptions(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.reportNames,
			repository: ReportRepo.retrieveReportNameOptions,
			repositoryParams: null
		})
	}

	@flow.bound
	*generateReport(reportId: number, data: DataReport.Request): CustomGenerator<void> {
		this.rootStore[STORE_UI].setIsBtnLoading(LoadingBtnNames.generateReport, true)
		try {
			yield ReportRepo.generateReport(reportId, data)
			this.rootStore[STORE_UI].showSuccessNotification(
				i18n.t('constants:REPORT_GENERATE_SUCCESS_MESSAGE')
			)
			this.rootStore[STORE_UI].closeModal()
			this.table.getItems()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsBtnLoading(null, false)
	}

	@flow.bound
	*retrieveReportData(id: number): CustomGenerator<DataReport.Detailed> {
		this.reportData.isLoading = true
		try {
			const report = yield ReportRepo.retrieveReportData(id)
			this.reportData.value = {
				...report,
				reportWhereParameters: formatReportParameters(
					report.reportWhereParameters
				) as DataReport.ReportWhereParameters[],
				sqlParameters: formatReportParameters(
					report.sqlParameters
				) as DataReport.SqlParameters[]
			}
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
			this.clearReportData()
		}
		this.reportData.isLoading = false
	}

	@action.bound
	clearReportData() {
		this.reportData = getDefaultStoreValue()
	}

	@flow.bound
	*generateDataEmailReport(
		reportId: number,
		reportData: DataReport.Request
	): CustomGenerator<void | string, void> {
		this.rootStore[STORE_UI].setIsBtnLoading(LoadingBtnNames.sendReportToEmail)
		try {
			yield ReportRepo.generateEmailReport(reportId, reportData)
			this.rootStore[STORE_UI].showSuccessNotification(
				i18n.t('constants:REPORT_EMAIL_GENERATE_SUCCESS_MESSAGE')
			)
			this.rootStore[STORE_UI].closeModal()
			this.table.getItems()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsBtnLoading(null, false)
	}

	@computed
	get reportNamesOptions() {
		return formatDataToLabelValue(this.reportNames.value)
	}

	@flow.bound
	*retrievePreviewOptions(
		parameterId: Key,
		query?: string
	): CustomGenerator<ValueDescription[], LabelValue[] | undefined> {
		try {
			const options = yield ReportRepo.retrievePreviewOptions(parameterId, query)
			return formatValueDescriptionDataToLabelValue(options)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow.bound
	*downloadReportLog(reportLogId: Key | null): CustomGenerator<Blob> {
		if (!reportLogId) {
			return
		}
		this.isReportLogDownloading = true

		try {
			const document = yield ReportRepo.downloadReport(reportLogId)
			openPdfDocumentFromBlob(document, reportLogId.toString())
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.isReportLogDownloading = false
		}
	}
}
